// SPDX-License-Identifier: MIT
// Desktop conversation history — durable, backed by a local JSON file via the
// Rust get_conversations / set_conversations commands (mirrors provider-store).
// The web converse edge fn reconstructs history from Postgres by conversation_id ;
// on desktop the bridge plays that role, and now the history SURVIVES app restart.
// Read-through / write-through (no module cache) keeps it simple + race-free for
// a single-user desktop app ; conversations.json is small.
import { tauriInvoke } from './tauri';

export interface StoredMessage {
  role: 'user' | 'assistant';
  content: string;
  // Assistant turns only — did this turn materially evolve the fiche, and to
  // which version. Mirrors public.messages.manifest_changed / version_number on
  // the web side so the "✓ fiche → vN" badge survives an app restart.
  manifest_changed?: boolean;
  version_number?: number | null;
}

type ConversationsFile = Record<string, StoredMessage[]>;

// One conversation thread per project (matches the web `.maybeSingle()`
// assumption). The id is DETERMINISTIC from the project id so the desktop
// data-bridge can re-find the thread after the in-memory chat cache is gone
// (app restart) — there is no project_id→conversation index on disk.
export function conversationIdForProject(projectId: string): string {
  return `conv-${projectId}`;
}

// Kept as the converse bridge's "open a thread for this project" call. Now an
// alias for the deterministic id so a first turn and a later re-hydration agree.
export function newConversationId(projectId: string): string {
  return conversationIdForProject(projectId);
}

async function readAll(): Promise<ConversationsFile> {
  const raw = await tauriInvoke()('get_conversations');
  if (typeof raw !== 'string' || raw.length === 0) return {};
  try {
    const obj = JSON.parse(raw) as ConversationsFile;
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

export async function getConversation(conversationId: string): Promise<StoredMessage[]> {
  return (await readAll())[conversationId] ?? [];
}

export async function appendTurn(
  conversationId: string,
  userMessage: string,
  assistantReply: string,
  assistantMeta?: { manifest_changed?: boolean; version_number?: number | null },
): Promise<void> {
  const all = await readAll();
  const history = all[conversationId] ?? [];
  history.push({ role: 'user', content: userMessage });
  // Spreading an undefined meta is a no-op, so a plain turn stays {role, content}.
  history.push({ role: 'assistant', content: assistantReply, ...assistantMeta });
  all[conversationId] = history;
  await tauriInvoke()('set_conversations', { json: JSON.stringify(all) });
}
